/**
 * Token storage for portal2.
 * Model (per MIGRATION_PLAN.md §1, §9):
 * - access token: held in memory AND mirrored to sessionStorage so a page
 * reload keeps the session alive WITHOUT a network `/v1/refresh` round-trip.
 * That per-reload refresh raced against the backend's refresh-token rotation
 * and logged the user out on a rapid double-refresh; persisting the token
 * makes a reload a no-op. sessionStorage (not localStorage) scopes the token
 * to the tab session — it's gone when the tab/window closes, so it lives no
 * longer than the session it belongs to. The user-service also sets an
 * HttpOnly `access_token` cookie which the browser sends automatically for
 * browser-initiated requests (file downloads, SSE) — we do not read or rely
 * on it from JS.
 * - refresh token: localStorage (interim). The user service does not yet set
 * a refresh-token cookie. A backend ticket is open to add one; once shipped,
 * the `getRefreshToken` / `setRefreshToken` / `clearRefreshToken` paths below
 * become no-ops and `/v1/refresh` is called with an empty body.
 * This is one of two modules permitted to touch web storage (the other is
 * `prefsStore` for non-sensitive UI preferences). ESLint enforces the boundary
 * (see eslint.config.js no-restricted-syntax rule + the per-file override).
 */

const REFRESH_TOKEN_KEY = 'om.refreshToken';
const ACCESS_SESSION_KEY = 'om.accessSession';

type Listener = () => void;

interface PersistedAccess {
  token: string;
  expiresAt: number;
}

/**
 * Read the persisted access token from sessionStorage. Tolerant of a missing,
 * corrupt, or hand-edited entry — anything that doesn't parse to the expected
 * shape is treated as "no session" rather than crashing app boot.
 */
function readPersistedAccess(): PersistedAccess | null {
  try {
    const raw = sessionStorage.getItem(ACCESS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAccess>;
    if (typeof parsed.token === 'string' && typeof parsed.expiresAt === 'number') {
      return { token: parsed.token, expiresAt: parsed.expiresAt };
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedAccess(token: string, expiresAt: number): void {
  try {
    sessionStorage.setItem(ACCESS_SESSION_KEY, JSON.stringify({ token, expiresAt }));
  } catch {
    // sessionStorage may be unavailable (private mode, quota); the in-memory
    // copy still serves this tab until the next reload.
  }
}

function clearPersistedAccess(): void {
  try {
    sessionStorage.removeItem(ACCESS_SESSION_KEY);
  } catch {
    // ignored
  }
}

// Hydrate the in-memory token from sessionStorage at module load so a reload
// restores the session before any component renders — no /v1/refresh needed.
const persistedAccess = readPersistedAccess();

let accessTokenInMemory: string | null = persistedAccess?.token ?? null;
let accessTokenExpiresAt: number | null = persistedAccess?.expiresAt ?? null;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

export const tokenStore = {
  getAccessToken(): string | null {
    return accessTokenInMemory;
  },

  getAccessTokenExpiresAt(): number | null {
    return accessTokenExpiresAt;
  },

  isAccessTokenExpired(now: number = Date.now()): boolean {
    if (accessTokenInMemory === null || accessTokenExpiresAt === null) return true;
    return now >= accessTokenExpiresAt;
  },

  setAccessToken(token: string, expiresInSeconds: number): void {
    accessTokenInMemory = token;
    accessTokenExpiresAt = Date.now() + expiresInSeconds * 1000;
    writePersistedAccess(accessTokenInMemory, accessTokenExpiresAt);
    notify();
  },

  clearAccessToken(): void {
    accessTokenInMemory = null;
    accessTokenExpiresAt = null;
    clearPersistedAccess();
    notify();
  },

  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  },

  setRefreshToken(token: string): void {
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } catch {
      // storage may be unavailable (private mode, quota); silently ignored —
      // session will still work until next reload.
    }
  },

  clearRefreshToken(): void {
    try {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // ignored
    }
  },

  clearAll(): void {
    this.clearAccessToken();
    this.clearRefreshToken();
  },

  /**
 * Subscribe to access-token changes. Returns an unsubscribe function.
 * Used by React adapters to re-render on login/logout.
 */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export type TokenStore = typeof tokenStore;
